/** HTTP API client with JWT auth token management. */

import { config } from '../config'

const API_BASE = config.apiBaseUrl

let accessToken: string | null = null
let refreshToken: string | null = null
let refreshPromise: Promise<boolean> | null = null
let pendingUserActivity = false

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

if (typeof window !== 'undefined') {
  const markUserActivity = () => {
    pendingUserActivity = true
  }
  for (const eventName of ['click', 'keydown', 'pointerdown', 'touchstart']) {
    window.addEventListener(eventName, markUserActivity, { passive: true })
  }
}

function consumeUserActivity(): boolean {
  const active = pendingUserActivity
  pendingUserActivity = false
  return active
}

function tokenStorage(): Storage {
  return sessionStorage
}

/** Store tokens from login response. */
export function setTokens(access: string, refresh: string) {
  accessToken = access
  refreshToken = refresh
  tokenStorage().setItem(ACCESS_TOKEN_KEY, access)
  tokenStorage().setItem(REFRESH_TOKEN_KEY, refresh)
  // Remove older persistent tokens if the user upgraded from the localStorage build.
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** Load tokens from sessionStorage on startup. */
export function loadTokens() {
  accessToken = tokenStorage().getItem(ACCESS_TOKEN_KEY)
  refreshToken = tokenStorage().getItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** Clear stored tokens (logout). */
export function clearTokens() {
  accessToken = null
  refreshToken = null
  tokenStorage().removeItem(ACCESS_TOKEN_KEY)
  tokenStorage().removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/** Revoke the backend refresh session and clear local tokens. */
export async function logout(): Promise<void> {
  const token = refreshToken
  try {
    if (token) {
      await fetchWithTimeout(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      })
    }
  } finally {
    clearTokens()
  }
}

/** Get the current access token. */
export function getAccessToken(): string | null {
  return accessToken
}

/** Check if we have tokens stored. */
export function hasTokens(): boolean {
  return accessToken !== null
}

const REQUEST_TIMEOUT_MS = 30_000

/** Create a fetch call with an AbortController timeout. */
function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  // If the caller already passed a signal, chain abort from it
  if (init?.signal) {
    init.signal.addEventListener('abort', () => controller.abort())
  }

  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timeoutId))
}

/** Fetch wrapper with automatic JWT auth and token refresh. */
async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers)
  const userActivity = consumeUserActivity()
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  if (userActivity) {
    headers.set('X-Aegis-User-Activity', 'true')
  }
  if (!headers.has('Content-Type') && init?.body) {
    headers.set('Content-Type', 'application/json')
  }

  let response = await fetchWithTimeout(`${API_BASE}${path}`, { ...init, headers })

  // If 401 and we have a refresh token, try refreshing
  if (response.status === 401 && refreshToken) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      headers.set('Authorization', `Bearer ${accessToken}`)
      response = await fetchWithTimeout(`${API_BASE}${path}`, { ...init, headers })
    }
  }

  return response
}

async function tryRefreshToken(): Promise<boolean> {
  if (!refreshToken) return false

  // Deduplicate concurrent refresh attempts — if a refresh is already
  // in-flight, all callers await the same promise instead of firing
  // multiple /auth/refresh requests (which would cause race-condition logouts).
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (!res.ok) {
        clearTokens()
        return false
      }

      const data = await res.json()
      setTokens(data.access_token, data.refresh_token)
      return true
    } catch {
      clearTokens()
      return false
    }
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

// ── Auth endpoints ──────────────────────────────────────────────────────────

export interface LoginResult {
  requires_totp: boolean
  partial_token: string | null
  tokens: { access_token: string; refresh_token: string; expires_in: number } | null
}

export async function login(password: string): Promise<LoginResult> {
  const res = await fetchWithTimeout(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Login failed' }))
    throw new Error(error.error || 'Login failed')
  }

  const data: LoginResult = await res.json()
  if (data.tokens) {
    setTokens(data.tokens.access_token, data.tokens.refresh_token)
  }
  return data
}

export async function verifyTotp(partialToken: string, code: string): Promise<void> {
  const res = await fetchWithTimeout(`${API_BASE}/auth/totp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ partial_token: partialToken, code }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'TOTP verification failed' }))
    throw new Error(error.error || 'TOTP verification failed')
  }

  const data = await res.json()
  setTokens(data.access_token, data.refresh_token)
}

export async function reauthTotp(code: string): Promise<void> {
  const res = await apiFetch('/auth/reauth', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Re-authentication failed' }))
    throw new Error(error.error || 'Re-authentication failed')
  }

  const data = await res.json()
  setTokens(data.access_token, data.refresh_token)
}

// ── Generic API methods ─────────────────────────────────────────────────────

function logApiError(method: string, path: string, status: number, errorMsg: string) {
  // Lazy import to avoid circular deps during module init
  const entry = { method, path, status, error: errorMsg, ts: new Date().toISOString() }
  try {
    const log = JSON.parse(localStorage.getItem('aegis_debug_log') || '[]')
    log.push({
      ts: entry.ts,
      level: 'error',
      tag: 'api',
      msg: `${method} ${path} → ${status}`,
      data: errorMsg,
    })
    if (log.length > 300) log.shift()
    localStorage.setItem('aegis_debug_log', JSON.stringify(log))
  } catch {
    /* ignore storage errors */
  }
  console.error(`[api] ${method} ${path} → ${status}: ${errorMsg}`)
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback
  const payload = error as { error?: unknown; detail?: unknown }
  const message =
    typeof payload.error === 'string' && payload.error.trim() ? payload.error : fallback
  if (typeof payload.detail === 'string' && payload.detail.trim()) {
    return `${message}: ${payload.detail}`
  }
  if (payload.detail && typeof payload.detail === 'object') {
    return `${message}: ${JSON.stringify(payload.detail)}`
  }
  return message
}

export async function get<T>(path: string): Promise<T> {
  const res = await apiFetch(path)
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }))
    const msg = apiErrorMessage(error, `HTTP ${res.status}`)
    logApiError('GET', path, res.status, msg)
    throw new Error(msg)
  }
  return normalizeApiData(await res.json()) as T
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }))
    const msg = apiErrorMessage(error, `HTTP ${res.status}`)
    logApiError('POST', path, res.status, msg)
    throw new Error(msg)
  }
  return normalizeApiData(await res.json()) as T
}

export async function put<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }))
    const msg = apiErrorMessage(error, `HTTP ${res.status}`)
    logApiError('PUT', path, res.status, msg)
    throw new Error(msg)
  }
  return normalizeApiData(await res.json()) as T
}

export async function del<T>(path: string): Promise<T> {
  const res = await apiFetch(path, { method: 'DELETE' })
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }))
    const msg = apiErrorMessage(error, `HTTP ${res.status}`)
    logApiError('DELETE', path, res.status, msg)
    throw new Error(msg)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return normalizeApiData(await res.json()) as T
}

function normalizeApiData(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeApiData)
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        normalizeApiData(val),
      ]),
    )
  }
  return value
}
